/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Token State - Loading state management and listeners
 */

import type { TokenLoadingListener, TokenLoadingState } from "./token-types";

// ============================================================================
// TOKEN LOADING STATE
// ============================================================================

/**
 * Global loading state for token fetch
 */
let tokenLoadingState: TokenLoadingState = {
  isLoading: false,
  activeRequests: 0,
  queuedRequests: 0,
};

/**
 * Listeners for token loading state changes
 */
const tokenLoadingListeners = new Set<TokenLoadingListener>();

/**
 * Update token loading state and notify listeners
 */
export function updateTokenLoadingState(
  updates: Partial<TokenLoadingState>,
): void {
  tokenLoadingState = { ...tokenLoadingState, ...updates };
  tokenLoadingListeners.forEach((listener) => listener(tokenLoadingState));
}

/**
 * Subscribe to token loading state changes
 * @returns Unsubscribe function
 */
export function subscribeToTokenLoading(
  listener: TokenLoadingListener,
): () => void {
  tokenLoadingListeners.add(listener);
  // Immediately call with current state
  listener(tokenLoadingState);
  // Return unsubscribe function
  return () => {
    tokenLoadingListeners.delete(listener);
  };
}

/**
 * Get current token loading state
 */
export function getTokenLoadingState(): TokenLoadingState {
  return { ...tokenLoadingState };
}

/**
 * Reset loading state (used by clearTokenCache)
 */
export function resetTokenLoadingState(): void {
  updateTokenLoadingState({
    isLoading: false,
    activeRequests: 0,
    queuedRequests: 0,
  });
}
